import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/movies';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  }, [location]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.3)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 group whitespace-nowrap"
          >
            <div className="w-9 h-9 flex items-center justify-center">
              <i className="ri-movie-2-line text-2xl text-primary-500"></i>
            </div>
            <span className="text-lg font-bold text-foreground-100 font-heading group-hover:text-primary-400 transition-colors">
              6080影视
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/')
                  ? 'text-primary-400'
                  : 'text-foreground-300 hover:text-foreground-100'
              }`}
            >
              首页
            </Link>
            <div className="relative group">
              <Link
                to="/category/action"
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname.startsWith('/category')
                    ? 'text-primary-400'
                    : 'text-foreground-300 hover:text-foreground-100'
                }`}
              >
                分类
                <i className="ri-arrow-down-s-line ml-1 text-xs align-middle"></i>
              </Link>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-background-100 border border-background-300/60 rounded-lg py-3 px-4 min-w-[360px] shadow-[0_8px_30px_rgba(0,0,0,0.4)]">
                  <div className="grid grid-cols-3 gap-1">
                    {categories.slice(1).map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        className="px-3 py-2 text-sm text-foreground-300 hover:text-primary-400 hover:bg-background-200 rounded-md transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <Link
              to="/about"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/about')
                  ? 'text-primary-400'
                  : 'text-foreground-300 hover:text-foreground-100'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center bg-background-200/80 rounded-full px-4 py-2 gap-2 border border-background-300/50">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-sm text-foreground-500"></i>
              </div>
              <input
                type="text"
                placeholder="搜索电影..."
                className="bg-transparent text-sm text-foreground-200 placeholder-foreground-600 outline-none w-40 focus:w-56 transition-all duration-300"
              />
            </div>
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-200 transition-colors"
            >
              <i className="ri-search-line text-lg text-foreground-300"></i>
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-200 transition-colors"
            >
              <i
                className={`text-lg text-foreground-300 ${
                  isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
                }`}
              ></i>
            </button>
          </div>
        </div>

        {isSearchOpen && (
          <div className="md:hidden pb-4 px-2">
            <div className="flex items-center bg-background-200/80 rounded-full px-4 py-2.5 gap-2 border border-background-300/50">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-sm text-foreground-500"></i>
              </div>
              <input
                type="text"
                placeholder="搜索电影..."
                className="bg-transparent text-sm text-foreground-200 placeholder-foreground-600 outline-none flex-1"
              />
            </div>
          </div>
        )}
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-100 border-t border-background-300/40 px-4 py-4">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              className="px-4 py-3 text-sm font-medium text-foreground-200 hover:bg-background-200 rounded-lg transition-colors"
            >
              首页
            </Link>
            <div className="px-4 py-2 text-xs text-foreground-600 font-medium">
              电影分类
            </div>
            {categories.slice(1).map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="px-6 py-2.5 text-sm text-foreground-300 hover:text-primary-400 hover:bg-background-200 rounded-lg transition-colors"
              >
                {cat.name}
              </Link>
            ))}
            <div className="border-t border-background-300/40 my-2"></div>
            <Link
              to="/about"
              className="px-4 py-3 text-sm font-medium text-foreground-200 hover:bg-background-200 rounded-lg transition-colors"
            >
              关于我们
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}